import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';
import { alpha } from '@mui/material/styles';
import { COLORS, fadeInUp } from '../results/ResultsDesignTokens';
import { useForecastingService } from '../../../hooks';
import DataPipelineTab from './DataPipelineTab';
import TrainingTab from './TrainingTab';
import ValidationTab from './ValidationTab';
import ForecastTab from './ForecastTab';

const TABS = [
    { id: 'data', label: 'Data' },
    { id: 'training', label: 'Training' },
    { id: 'validation', label: 'Validation' },
    { id: 'forecast', label: 'Forecast' },
];

const TabButton = ({ label, isActive, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            px: 2,
            py: 1,
            cursor: 'pointer',
            borderBottom: '2px solid',
            borderBottomColor: isActive ? COLORS.accent : 'transparent',
            transition: 'all 0.2s ease',
            '&:hover': {
                borderBottomColor: isActive ? COLORS.accent : alpha(COLORS.accent, 0.4),
            },
        }}
    >
        <Typography sx={{
            fontFamily: vortxTypography.mono,
            fontSize: '0.75rem',
            fontWeight: isActive ? 600 : 400,
            color: isActive ? COLORS.accent : COLORS.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            textShadow: isActive ? `0 0 8px ${alpha(COLORS.accent, 0.6)}` : 'none',
            transition: 'all 0.2s ease',
        }}>
            {label}
        </Typography>
    </Box>
);

export default function ForecastingSection() {
    const [activeTab, setActiveTab] = useState('data');
    const service = useForecastingService();

    const renderTab = () => {
        switch (activeTab) {
            case 'data':
                return <DataPipelineTab service={service} />;
            case 'training':
                return <TrainingTab service={service} />;
            case 'validation':
                return <ValidationTab service={service} />;
            case 'forecast':
                return <ForecastTab service={service} />;
            default:
                return <DataPipelineTab service={service} />;
        }
    };

    return (
        <Box sx={{ animation: `${fadeInUp} 0.4s ease-out` }}>
            {/* Header */}
            <Typography sx={{
                fontFamily: vortxTypography.heading,
                fontSize: '1.5rem',
                fontWeight: 700,
                color: COLORS.text.primary,
                mb: 1,
            }}>
                Component Forecasting
            </Typography>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.75rem',
                color: COLORS.text.secondary,
                mb: 3,
            }}>
                ComStock-trained load component models for cold-start site forecasting
            </Typography>

            {/* Tab Bar */}
            <Box sx={{
                display: 'flex',
                gap: 0,
                borderBottom: `1px solid ${alpha(COLORS.accent, 0.2)}`,
                mb: 3,
            }}>
                {TABS.map(tab => (
                    <TabButton
                        key={tab.id}
                        label={tab.label}
                        isActive={activeTab === tab.id}
                        onClick={() => setActiveTab(tab.id)}
                    />
                ))}
            </Box>

            {/* Error banner */}
            {service.error && (
                <Box sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: alpha(COLORS.loss, 0.1),
                    border: `1px solid ${alpha(COLORS.loss, 0.3)}`,
                    borderRadius: 1,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        color: COLORS.loss,
                    }}>
                        {service.error}
                    </Typography>
                </Box>
            )}

            {/* Active Tab */}
            {renderTab()}
        </Box>
    );
}
